import { useMemo } from 'react';
import useAppStore from '../index'; 
import { 
    ClientDataState, 
    SettingsState, 
    EmployeeData, 
    MonatsDaten, 
    PrognoseZeile, 
    MitarbeiterLeistungData, 
    MitarbeiterStatus
} from '../types';
import { safeDiv } from '../utils';
import { formatMoney, resolveCurrencyCode } from '../../utils/currencyFormatter';
import { defaultClientData } from '../slices/coreDataSlice';
import { defaultSettings, normalizeLandCode, resolveActiveCountry } from '../slices/settingsSlice';
import { isSoloModeActive } from '../../utils/soloMode';
import { applyUnproductiveSplitToPersonalLists, resolvePersonnelCostMetrics } from '../../utils/personnelCostAdapter';
import { foerderSatz, summeNeutralerFoerderzuschuss } from '../../utils/foerderzuschuss';

export const DEFAULT_MANDANTEN_PARAMETER = {
    wochensatz: 4.33,
    auslastungsfaktor: 75,
    auslastungsfaktorMax: 100,
    zusatzumsatz: 30,
    gewinnlinie: 26,
    investlinie: 5,
    verlust1: 15,
    liquiditaet: 10
};

const parseVal = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        const clean = val.replace(/\./g, '').replace(',', '.');
        const num = parseFloat(clean);
        return isNaN(num) ? 0 : num;
    }
    return 0;
};

const resolveCountrySettings = (settingsObj: any, landCode: string) => {
    if (!settingsObj) return {};
    
    const possiblePaths = [
        settingsObj?.countries?.[landCode],
        settingsObj?.laender?.[landCode],
        settingsObj?.Länder?.[landCode],
        settingsObj?.[landCode],
        settingsObj?.global?.countries?.[landCode],
        settingsObj?.global?.laender?.[landCode]
    ];

    if (Array.isArray(settingsObj?.countries)) {
        possiblePaths.push(settingsObj.countries.find((c: any) => c.code === landCode || c.id === landCode));
    }
    if (Array.isArray(settingsObj?.laender)) {
        possiblePaths.push(settingsObj.laender.find((c: any) => c.code === landCode || c.id === landCode));
    }

    const found = possiblePaths.find(path => path && typeof path === 'object' && Object.keys(path).length > 0);
    return found || {};
};

const calculatePersonalData = (clientData: ClientDataState, settings: SettingsState, activeCountrySettings: any, activeLand: string) => {
    const wochensatz = clientData.wochensatz ?? DEFAULT_MANDANTEN_PARAMETER.wochensatz;
    const landCode = normalizeLandCode(activeLand);

    const mapEmployeeData = (
        employeeArray: Partial<EmployeeData>[],
        tableScope: 'produktiv' | 'unproduktiv' | 'azubi'
    ) => {
        return (employeeArray || []).map((ma): EmployeeData => {
            const wochenstunden = parseVal(ma.wochenstunden); 
            const arbeitstage = parseVal(ma.arbeitstage) || 5; 
            const monatsstunden = wochenstunden * wochensatz; 

            const costMetrics = resolvePersonnelCostMetrics(
                ma as any,
                {
                    selectedCountryId: settings?.selectedCountryId,
                    clientLand: activeLand || (clientData as any)?.land || (clientData as any)?.Land || landCode,
                    countrySettings: activeCountrySettings,
                },
                tableScope
            );

            // In IT_BZ ist baseWage das vereinbarte Netto ("Netto in busta"), in DE das Brutto-Grundgehalt.
            const grundgehalt = costMetrics.baseWage;
            const lohnzusatz = costMetrics.lohnzusatz;
            const bonus = costMetrics.bonus;
            const grundlohn = costMetrics.grundlohn;
            const bruttolohnGesamt = costMetrics.gesamtlohnBasis;

            const stundenlohnGrund = monatsstunden > 0 ? grundlohn / monatsstunden : 0; 
            const stundenlohnGesamt = monatsstunden > 0 ? bruttolohnGesamt / monatsstunden : 0; 

            const lohnnebenkostenSatz = costMetrics.lohnnebenkostenSatz;
            const lohnnebenkosten = costMetrics.lohnnebenkosten;
            const totalCost = costMetrics.totalPersonnelCost;
            const zugehoerigkeit = ma.zugehoerigkeit || 'Bitte auswählen';
            
            return { 
                ...ma, 
                id: ma.id || '', 
                name: ma.name || '', 
                grundgehalt, 
                lohnzusatz, 
                bonus, 
                wochenstunden, 
                arbeitstage, 
                taetigkeit: ma.taetigkeit || '', 
                zugehoerigkeit, 
                monatsstunden, 
                grundlohn, 
                bruttolohnGesamt, 
                stundenlohnGrund, 
                stundenlohnGesamt, 
                lohnnebenkostenSatz, 
                lohnnebenkosten, 
                totalCost,
                totalPersonnelCost: totalCost,
                country: costMetrics.isItBz ? 'IT_BZ' : (ma.country || landCode),
                grossWage: costMetrics.isItBz ? ma.grossWage : grundgehalt,
                netWage: costMetrics.isItBz ? grundgehalt : ma.netWage,
                costoAziendaleOverride: parseVal(ma.costoAziendaleOverride) || undefined,
            };
        });
    };

    const { effectivePersonalData, effectiveUnproductiveData } = applyUnproductiveSplitToPersonalLists(
        clientData?.personalData || [],
        clientData?.unproductivePersonalData || []
    );

    return {
        personalData: mapEmployeeData(effectivePersonalData, 'produktiv'),
        unproductivePersonalData: mapEmployeeData(effectiveUnproductiveData, 'unproduktiv'),
        azubiAssistenzData: mapEmployeeData(clientData?.azubiAssistenzData || [], 'azubi'),
    };
};

export const defaultCalculations = {
    ready: false, 
    formatCurrency: (value: number) => formatMoney(value, 'EUR'), 
    mwstSatz: 0, 
    currencyCode: 'EUR', 
    zeitraum: 12, 
    gesamtUmsatz: 0, 
    monatsumsatzNetto: 0, 
    aggregatedMonatsumsatzNetto: 0,
    isTeamProPlus: false,
    monatsumsatzBrutto: 0, 
    sollumsatz: 0, 
    ueberschuss: 0, 
    ueberschussProzent: 0, 
    materialkostenMonat: 0, 
    geraetekostenMonat: 0, 
    gemeinkostenGesamtMonat: 0, 
    produktiveKostenGesamtMonat: 0, 
    unprodUnternehmerlohn: 0, 
    unprodUnternehmerlohnPercent: 0, 
    einkommenssteuerUnternehmer: 0, 
    unproduktiveLohnkostenMitarbeiter: 0, 
    azubiKostenGesamt: 0, 
    prodUnternehmerlohn: 0, 
    baseMaterialProzent: 0, 
    geraetekostenGesamt: 0, 
    betriebskostenMonat: 0, 
    positiveWerteMonat: 0, 
    produktiveLohnkostenMitarbeiter: 0, 
    produktiveLohnkostenMitarbeiterOhneAzubis: 0,
    gemeinkostenfaktor: 0, 
    materialkostenfaktor: 0, 
    geraetekostenfaktor: 0, 
    prodLohnkostenfaktor: 0, 
    einnahmenProMonat: 0, 
    ausgabenProMonat: 0, 
    multiplikator: 0, 
    stundenlohnChef: 0, 
    kalkSollumsatzInklFortzahlung: 0, 
    lohnfaktorNetto: 0, 
    lohnfaktorBrutto: 0, 
    lohnfaktorFortzahlung: 0, 
    lohnfaktorAktiv: 0, 
    planumsatzGesamtNetto: 0,
    planumsatzGesamtNettoKalkuliert: 0,
    differenz: 0,
    differenzKalkuliert: 0,
    produktiveKostenTabelle: 0, 
    lohnfortzahlung: 0, 
    durchschnittlicherUmsatzProTag: 0, 
    kalkulierterUnternehmerUmsatzBrutto: 0, 
    calculatedMitarbeiterLeistung: {} as Record<string, MitarbeiterLeistungData & { requiredRate75?: number }>, 
    unternehmerMetrics: { efficiency: 0, umsatzauslastung: 0, vkFaktor: 0, sumKrankTage: 0 },
    realUnternehmerSchnittHJ1: 0,
    realUnternehmerSchnittHJ2: 0,
    gesamtlohn: 0,
    echterBedarfGesamt: 0,
    bruttoumsatzProStunde: 0,
    erwarteterBruttoUmsatz: 0,
    erwarteterNettoUmsatz: 0,
    erwarteteEinkommenssteuer: 0,
    einkommenssteuerSatz: 0,
    wochensatz: 4.33,
    geraeteListe: [] as any[],
    verlustPercent: 0,
    gemeinkostenPercent: 0,
    teamStundenlohn: 0,
    avgSozialabgabenPercent: 0,
    lohnfortzahlungPercent: 0,
    realisierterStundenlohnChef: 0,
    prodWochenstundenChef: 0,
    monatsstundenChef: 0,
    inhaberErtragSoll: 0,
    rentabilitaetSoll: 0,
    inhaberErtragIst: 0,
    rentabilitaetIst: 0,
    durchschnittsUmsatzIstNetto: 0,
    processedULHJ1: { monatsDetails: [], summary: {}, average: {}, umsatzAuslastung: 0 },
    processedULHJ2: { monatsDetails: [], summary: {}, average: {}, umsatzAuslastung: 0 }
};

export interface SwissVatEvaluation {
    effectiveRate: number;
    standardRate: number;
    vatThreshold: number;
    isVatExempt: boolean;
    voluntaryOption: boolean;
    vorjahresUmsatz: number;
}

export const getEffectiveSwissVatRate = (
    activeCountrySettings: any,
    safeClientData: any,
    vorjahresUmsatz: number
): SwissVatEvaluation => {
    const dbMwSt =
        activeCountrySettings?.MwSt_Satz ??
        activeCountrySettings?.mwst ??
        activeCountrySettings?.mwstSatz ??
        activeCountrySettings?.MwSt;
    const standardRate = dbMwSt !== undefined && dbMwSt !== null && Number(dbMwSt) > 0 ? Number(dbMwSt) : 8.1;
    const vatThreshold = Number(activeCountrySettings?.vatThreshold) > 0 ? Number(activeCountrySettings.vatThreshold) : 100000;
    const voluntaryOption = Boolean(activeCountrySettings?.voluntaryOption);
    const umsatz = Number(vorjahresUmsatz) || 0;

    const isMandantUstBefreit = Boolean(
        safeClientData?.umsatzsteuerbefreit === true ||
        safeClientData?.isUmsatzsteuerbefreit === true ||
        safeClientData?.isVatExempt === true ||
        (safeClientData as any)?.clientData?.umsatzsteuerbefreit === true ||
        (safeClientData as any)?.clientData?.isUmsatzsteuerbefreit === true ||
        (safeClientData as any)?.clientData?.isVatExempt === true
    );

    // PRIO 0: Universeller Hard-Override aus der Mandantenakte
    if (isMandantUstBefreit) {
        return {
            effectiveRate: 0.0,
            standardRate,
            vatThreshold,
            isVatExempt: true,
            voluntaryOption: false,
            vorjahresUmsatz: umsatz,
        };
    }

    const settingIsVatExempt =
        activeCountrySettings?.isVatExempt !== undefined
            ? Boolean(activeCountrySettings.isVatExempt)
            : true;

    if (voluntaryOption || settingIsVatExempt === false) {
        return {
            effectiveRate: standardRate,
            standardRate,
            vatThreshold,
            isVatExempt: false,
            voluntaryOption,
            vorjahresUmsatz: umsatz,
        };
    }

    if (umsatz >= vatThreshold) {
        return {
            effectiveRate: standardRate,
            standardRate,
            vatThreshold,
            isVatExempt: false,
            voluntaryOption: false,
            vorjahresUmsatz: umsatz,
        };
    }

    return {
        effectiveRate: 0.0,
        standardRate,
        vatThreshold,
        isVatExempt: true,
        voluntaryOption: false,
        vorjahresUmsatz: umsatz,
    };
};

export const calculateAllMetrics = (safeClientData: any, safeSettings: any) => {
    const rawLand = safeClientData?.Land || safeClientData?.land || (safeClientData as any)?.clientData?.Land || (safeClientData as any)?.clientData?.land || safeSettings?.selectedCountryId || 'DE';
    const activeLand = normalizeLandCode(rawLand);

    const isMandantUstBefreit = Boolean(
        safeClientData?.umsatzsteuerbefreit === true ||
        safeClientData?.isUmsatzsteuerbefreit === true ||
        safeClientData?.isVatExempt === true ||
        (safeClientData as any)?.clientData?.umsatzsteuerbefreit === true ||
        (safeClientData as any)?.clientData?.isUmsatzsteuerbefreit === true ||
        (safeClientData as any)?.clientData?.isVatExempt === true
    );

    const wochensatz = safeClientData.wochensatz ?? DEFAULT_MANDANTEN_PARAMETER.wochensatz;
    const auslastungsfaktor = safeClientData.auslastungsfaktor ?? DEFAULT_MANDANTEN_PARAMETER.auslastungsfaktor;
    const auslastungsfaktorMax = safeClientData.auslastungsfaktorMax ?? DEFAULT_MANDANTEN_PARAMETER.auslastungsfaktorMax;
    const zusatzumsatz = safeClientData.zusatzumsatz ?? DEFAULT_MANDANTEN_PARAMETER.zusatzumsatz;
    const gewinnlinie = safeClientData.gewinnlinie ?? DEFAULT_MANDANTEN_PARAMETER.gewinnlinie;
    const investlinie = safeClientData.investlinie ?? DEFAULT_MANDANTEN_PARAMETER.investlinie;
    const verlust1 = safeClientData.verlust1 ?? DEFAULT_MANDANTEN_PARAMETER.verlust1;
    const liquiditaet = safeClientData.liquiditaet ?? DEFAULT_MANDANTEN_PARAMETER.liquiditaet;

    const baseCountrySettings = resolveCountrySettings(safeSettings, activeLand);
    const countryOverrides = safeSettings?.countryOverrides?.[activeLand] || {};
    const resolvedCountryDefaults = resolveActiveCountry(safeSettings, safeClientData);
    const activeCountrySettings = { ...resolvedCountryDefaults, ...baseCountrySettings, ...countryOverrides };

    const currencyCode = resolveCurrencyCode(
        activeCountrySettings?.currency || (activeLand === 'CH' ? 'CHF' : 'EUR'),
        activeLand
    );
    
    const formatCurrency = (value: number, decimals = 2) =>
        formatMoney(value || 0, currencyCode, decimals);

    const safeBwaData = safeClientData.bwaData || defaultClientData.bwaData;
    const safeLohnfaktorData = safeClientData.lohnfaktorData || defaultClientData.lohnfaktorData;
    const safeUnternehmerData = safeClientData.unternehmerData || defaultClientData.unternehmerData;

    const vorjahresUmsatz = parseVal(safeBwaData.umsatz);
    const chVatStatus = activeLand === 'CH'
        ? getEffectiveSwissVatRate(activeCountrySettings, safeClientData, vorjahresUmsatz)
        : null;

    const dbMwSt = activeCountrySettings?.MwSt_Satz ?? activeCountrySettings?.mwst ?? activeCountrySettings?.mwstSatz ?? activeCountrySettings?.MwSt;
    const defaultMwSt = activeLand === 'CH' ? 8.1 : activeLand === 'AT' ? 20 : activeLand === 'IT' ? 22 : 19;
    // PRIO 0: Universeller Hard-Override für ALLE Länder (DE, AT, CH, IT_BZ)
    const effectiveMwstSatz = isMandantUstBefreit
        ? 0.0
        : activeLand === 'CH' && chVatStatus
            ? chVatStatus.effectiveRate
            : (dbMwSt !== undefined ? Number(dbMwSt) : defaultMwSt);

    const { personalData, unproductivePersonalData, azubiAssistenzData } = calculatePersonalData(safeClientData, safeSettings, activeCountrySettings, activeLand);

    // GbR-Weiche: Finanzbedarf/Steuer/Stunden/Umsatz aus Partner-Summe, sonst unternehmerData
    // Hermetische Trennung: bei isGbRAktiv KEIN Fallback auf unternehmerData für diese Kennzahlen
    const pg = (safeClientData as any).personengesellschaft;
    const pgPartners = Array.isArray(pg?.partner) ? pg.partner : [];
    const isGbRAktiv = pg?.aktiv === true && pgPartners.length > 0;

    let basisEntnahme = 0;
    let basisSozial = 0;
    let steuerSatz = 0;
    let echterBedarfGesamt = 0;
    let steuerBetrag = 0;
    let prodWochenstundenChef = 0;
    let avgUmsatzInput = 0;
    let monatsUmsaetze: number[] = [];

    if (isGbRAktiv) {
        const monatsSumme = Array(12).fill(0);

        pgPartners.forEach((p: any) => {
            const pEntnahme = parseVal(p?.entnahme);
            const pSozial = parseVal(p?.sozialkosten);
            const pBedarf = pEntnahme + pSozial;
            const pSteuerSatz = parseVal(p?.einkommenssteuer);
            basisEntnahme += pEntnahme;
            basisSozial += pSozial;
            steuerBetrag += pBedarf * (pSteuerSatz / 100);
            prodWochenstundenChef += parseVal(p?.prodWochenstunden);
            avgUmsatzInput += parseVal(p?.durchschnittsumsatz);

            const pMonate = Array.isArray(p?.monatsUmsaetze) ? p.monatsUmsaetze : [];
            for (let i = 0; i < 12; i++) {
                monatsSumme[i] += parseVal(pMonate[i]);
            }
        });

        monatsUmsaetze = monatsSumme;
        echterBedarfGesamt = basisEntnahme + basisSozial;
        steuerSatz = echterBedarfGesamt > 0 ? (steuerBetrag / echterBedarfGesamt) * 100 : 0;
    } else {
        basisEntnahme = parseVal(safeUnternehmerData.entnahme);
        basisSozial = parseVal(safeUnternehmerData.sozialkosten);
        steuerSatz = parseVal(safeUnternehmerData.einkommenssteuer);
        echterBedarfGesamt = basisEntnahme + basisSozial;
        steuerBetrag = echterBedarfGesamt * (steuerSatz / 100);
        prodWochenstundenChef = parseVal(safeUnternehmerData.prodWochenstunden);
        avgUmsatzInput = parseVal(safeUnternehmerData.durchschnittsumsatz);
        monatsUmsaetze = Array.isArray(safeUnternehmerData.monatsUmsaetze)
            ? safeUnternehmerData.monatsUmsaetze
            : [];
    }

    let kalkulierterUnternehmerUmsatzBrutto = avgUmsatzInput;
    
    if (kalkulierterUnternehmerUmsatzBrutto <= 1) { 
        const eingegebeneUmsaetze = monatsUmsaetze.map(parseVal).filter((u: number) => u > 0); 
        if (eingegebeneUmsaetze.length > 0) { 
            const summeMonate = eingegebeneUmsaetze.reduce((sum: number, val: number) => sum + val, 0); 
            kalkulierterUnternehmerUmsatzBrutto = summeMonate / eingegebeneUmsaetze.length; 
        } else {
            kalkulierterUnternehmerUmsatzBrutto = echterBedarfGesamt > 0 ? echterBedarfGesamt : 0;
        }
    }
    
    const globalSettings = safeSettings?.global || {}; 
    const zeitraum = safeBwaData.zeitraum || 12; 
    
    const bwaUmsatz = parseVal(safeBwaData.umsatz); 
    const prognoseUmsatz = (safeBwaData.prognoseZeilen || []).filter((z: PrognoseZeile) => z.aktiv).reduce((sum: number, z: PrognoseZeile) => sum + (z.jahresumsatz || 0), 0); 
    const gesamtUmsatz = bwaUmsatz + prognoseUmsatz; 
    const monatsumsatzNetto = zeitraum > 0 ? gesamtUmsatz / zeitraum : 0; 
    const monatsumsatzBrutto = monatsumsatzNetto * (1 + effectiveMwstSatz / 100); 
    
    const produktiveLohnkostenMitarbeiterSumme = personalData.reduce((sum: number, ma: EmployeeData) => sum + (ma.totalCost || 0), 0); 
    const unproduktiveLohnkostenMitarbeiter = unproductivePersonalData.reduce((sum: number, ma: EmployeeData) => sum + (ma.totalCost || 0), 0); 
    const azubiKostenGesamt = azubiAssistenzData.reduce((sum: number, ma: EmployeeData) => sum + (ma.totalCost || 0), 0); 
    
    const unternehmerlohnGesamt = echterBedarfGesamt; 
    const einkommenssteuerUnternehmer = steuerBetrag; 
    
    // Stunden strikt aus Weiche (GbR = Partner-Summe, sonst Einzel) — kein Querzugriff auf die andere Struktur
    const monatsstundenChef = prodWochenstundenChef * wochensatz; 
    // unternehmerStundenlohn_editable bleibt salonweit geteilt (UI-Freeze); Isolation läuft über die Stundenbasis
    const unternehmerStundenlohnSoll = parseVal(safeLohnfaktorData.unternehmerStundenlohn_editable); 
    
    const prodUnternehmerlohnCalc = monatsstundenChef * unternehmerStundenlohnSoll;
    let prodUnternehmerlohn = (prodUnternehmerlohnCalc > 0) ? prodUnternehmerlohnCalc : unternehmerlohnGesamt;
    let unprodUnternehmerlohn = Math.max(0, unternehmerlohnGesamt - prodUnternehmerlohn);

    // Solo: kein unproduktiver Unternehmerlohn — harte 0 (kein 1-€-Rundungsrest)
    const isSoloMandant =
        isSoloModeActive(safeClientData) ||
        !Array.isArray(personalData) ||
        personalData.length === 0;
    if (isSoloMandant) {
        prodUnternehmerlohn = unternehmerlohnGesamt;
        unprodUnternehmerlohn = 0;
    }

    const kostendeckungsfaktor = parseVal(safeLohnfaktorData.kostendeckungsfaktor1) || 2.8;
    const kostendeckungsumsatz = prodUnternehmerlohn * kostendeckungsfaktor;

    const betriebskostenKeys = ['raumkosten', 'betriebssteuern', 'versicherungen', 'besondereKosten', 'kfzKosten', 'werbeReisekosten', 'warenabgabe', 'reparatur', 'sonstigeKosten', 'kredite', 'zinsaufwendungen', 'gewerbesteuer', 'koerperschaftssteuer', 'budgetPlansumme', 'preisExpert', 'premiumSoftware', 'sonstigeEintrag1', 'sonstigeEintrag2', 'sonstigeEintrag3', 'sonstigeEintrag4']; 
    const positiveWerteKeys = ['abschreibungen', 'zinsenMiete']; 
    const geraeteKeys = ['geraetEintrag1', 'geraetEintrag2', 'geraetEintrag3', 'geraetEintrag4', 'geraetEintrag5', 'geraetEintrag6', 'geraetEintrag7', 'geraetEintrag8', 'geraetEintrag9', 'geraetEintrag10'];
    
    const geraeteListe = geraeteKeys.map(key => {
        const label = (safeBwaData as any)[`${key}_label`];
        const periodCost = parseVal((safeBwaData as any)[key]);
        if (!label || label.trim() === '') return null;
        const monthlyCost = zeitraum > 0 ? periodCost / zeitraum : 0;
        const kostenProzent = monatsumsatzNetto > 0 ? (monthlyCost / monatsumsatzNetto) * 100 : 0;
        return { id: key, name: label, kostenProzent: kostenProzent };
    }).filter(g => g !== null);

    const betriebskostenGesamt = betriebskostenKeys.reduce((sum, key) => sum + parseVal((safeBwaData as any)[key]), 0); 
    const positiveWerteGesamt = positiveWerteKeys.reduce((sum, key) => sum + parseVal((safeBwaData as any)[key]), 0); 
    const betriebskostenMonat = zeitraum > 0 ? betriebskostenGesamt / zeitraum : 0; 
    const positiveWerteMonat = zeitraum > 0 ? positiveWerteGesamt / zeitraum : 0; 
    const baseMaterialProzent = bwaUmsatz > 0 ? (parseVal(safeBwaData.material) / bwaUmsatz) * 100 : 0; 
    const skalierterMaterialEinsatzEuro = gesamtUmsatz * (baseMaterialProzent / 100); 
    const materialkostenMonat = zeitraum > 0 ? skalierterMaterialEinsatzEuro / zeitraum : 0; 
    const geraetekostenGesamtSum = geraeteKeys.reduce((sum, key) => sum + parseVal((safeBwaData as any)[key]), 0); 
    const geraetekostenMonat = zeitraum > 0 ? geraetekostenGesamtSum / zeitraum : 0; 
    
    const kostenSatz = (ma: EmployeeData) => foerderSatz(ma);
    const unprodFoerderAbzug = unproductivePersonalData.reduce(
        (sum: number, ma: EmployeeData) => sum + (ma.totalCost || 0) * kostenSatz(ma),
        0
    );
    const gemeinkostenGesamtMonat = Math.max(
        0,
        betriebskostenMonat + unproduktiveLohnkostenMitarbeiter + einkommenssteuerUnternehmer + (azubiKostenGesamt * 0.3333333333) - positiveWerteMonat - unprodFoerderAbzug
    );
    const produktiveKostenGesamtMonat = produktiveLohnkostenMitarbeiterSumme + prodUnternehmerlohn + (azubiKostenGesamt * 0.6666666667);
    const sollumsatz = gemeinkostenGesamtMonat + produktiveKostenGesamtMonat + materialkostenMonat + geraetekostenMonat + unprodUnternehmerlohn;
    const foerderSchluss = summeNeutralerFoerderzuschuss([...personalData, ...azubiAssistenzData]);
    const foerderzuschussMonat = foerderSchluss + unprodFoerderAbzug;
    const sollumsatzVorFoerderung = sollumsatz + unprodFoerderAbzug;
    const breakEvenNachFoerderung = Math.max(0, sollumsatz - foerderSchluss);
    
    const unprodUnternehmerlohnPercent = monatsumsatzNetto > 0 ? (unprodUnternehmerlohn / monatsumsatzNetto) * 100 : 0;
    const gemeinkostenPercent = monatsumsatzNetto > 0 ? (gemeinkostenGesamtMonat / monatsumsatzNetto) * 100 : 0;
    const verlustPercent = verlust1; 

    const ueberschuss = monatsumsatzNetto - breakEvenNachFoerderung; 
    const ueberschussProzent = monatsumsatzNetto > 0 ? (ueberschuss / monatsumsatzNetto) * 100 : 0; 

    // Nur Rentabilitäts-Kennzahl (Kurzauswertung) — Sollumsatz/Steuer in der Kalkulation unverändert.
    // Plus: Überschuss + voller Bedarf + volle Steuer.
    // Unterdeckung: Minus frisst zuerst den Lohn — Steuer am auszahlbaren Lohn neu (Satz × Lohn).
    // Erst wenn kein Gehalt mehr bleibt, geht der Ertrag ins Minus (ohne Steuer-Add-back).
    let inhaberErtragSoll: number;
    if (ueberschuss >= 0) {
        inhaberErtragSoll = ueberschuss + echterBedarfGesamt + steuerBetrag;
    } else {
        const auszahlbarerLohn = echterBedarfGesamt + ueberschuss;
        if (auszahlbarerLohn >= 0) {
            const steuerEffektiv = auszahlbarerLohn * (steuerSatz / 100);
            inhaberErtragSoll = auszahlbarerLohn + steuerEffektiv;
        } else {
            inhaberErtragSoll = auszahlbarerLohn;
        }
    }
    const rentabilitaetSoll = monatsumsatzNetto > 0 ? (inhaberErtragSoll / monatsumsatzNetto) * 100 : 0;

    const gesamtleistungData = safeClientData.gesamtleistungData || [];
    const relevanteMonate = gesamtleistungData.filter((m: any) => (parseVal(m.bedienumsatzBrutto) + parseVal(m.vkUmsatzBrutto)) > 0);
    
    let durchschnittsUmsatzIstNetto = 0;
    let rentabilitaetIst = 0;
    let inhaberErtragIst = 0;
    
    if (relevanteMonate.length > 0) {
        const summeBrutto = relevanteMonate.reduce((sum: number, m: any) => sum + parseVal(m.bedienumsatzBrutto) + parseVal(m.vkUmsatzBrutto), 0);
        const avgBrutto = summeBrutto / relevanteMonate.length;
        durchschnittsUmsatzIstNetto = avgBrutto / (1 + effectiveMwstSatz / 100);
        
        const m1 = parseVal(safeLohnfaktorData.mindestumsatz1) || sollumsatz;
        const m2 = parseVal(safeLohnfaktorData.mindestumsatz2) || sollumsatz;

        const summeSollNetto = relevanteMonate.reduce((sum: number, m: any, indexInFiltered: number) => {
            const originalIndex = gesamtleistungData.indexOf(m);
            const currentBaseMindestumsatz = originalIndex < 6 ? m1 : m2;
            const anpassungsFaktor = m.kostenErhoeht ? -1 : 1;
            const angepassterMonatsSoll = currentBaseMindestumsatz + (parseVal(m.aussergewoehnlich) * anpassungsFaktor);
            return sum + angepassterMonatsSoll;
        }, 0);
        const avgSollNetto = summeSollNetto / relevanteMonate.length;

        const realUeberschuss = durchschnittsUmsatzIstNetto - avgSollNetto;
        inhaberErtragIst = echterBedarfGesamt + steuerBetrag + realUeberschuss;
        rentabilitaetIst = durchschnittsUmsatzIstNetto > 0 ? (inhaberErtragIst / durchschnittsUmsatzIstNetto) : 0;
    }

    // Alle Kostenstruktur-Faktoren einheitlich auf Netto-Umsatz (wie Lohnfaktor-Detailansicht).
    // Früher gemischt: GK÷Umsatz, Material/Geräte/Lohn÷Sollumsatz → Kurzauswertung weicht ab.
    const gemeinkostenfaktor = monatsumsatzNetto > 0 ? gemeinkostenGesamtMonat / monatsumsatzNetto : 0; 
    const materialkostenfaktor = monatsumsatzNetto > 0 ? materialkostenMonat / monatsumsatzNetto : 0; 
    const geraetekostenfaktor = monatsumsatzNetto > 0 ? geraetekostenMonat / monatsumsatzNetto : 0; 
    const prodLohnkostenfaktor = monatsumsatzNetto > 0 ? produktiveKostenGesamtMonat / monatsumsatzNetto : 0;
    const produktiveMitarbeiterOhneAzubis = personalData.filter((ma: EmployeeData) => !['Azubi', 'Prod. Azubi', 'produktiver Azubi'].includes(ma.taetigkeit || '')); 
    const produktiveLohnkostenMitarbeiterOhneAzubis = produktiveMitarbeiterOhneAzubis.reduce((sum: number, ma: EmployeeData) => sum + (ma.totalCost || 0), 0); 
    let produktiveKostenTabelle: number;
    if (activeLand === 'IT') {
        const produktiveLoehneNettoBasis = produktiveMitarbeiterOhneAzubis.reduce(
            (sum: number, ma: EmployeeData) => sum + (ma.grundlohn || 0) + (ma.bonus || 0),
            0
        );
        produktiveKostenTabelle = (produktiveLoehneNettoBasis * 1.225) + prodUnternehmerlohn;
    } else {
        // DE / Standard: unverändert auf ma.totalCost
        produktiveKostenTabelle = produktiveLohnkostenMitarbeiterOhneAzubis + prodUnternehmerlohn;
    }
    
    const lohnfortzahlung = produktiveLohnkostenMitarbeiterOhneAzubis * ((globalSettings.Urlaubsausgleich || 20) / 100); 
    
    const kalkSollumsatzInklFortzahlung = sollumsatz + lohnfortzahlung; 
    const lohnfaktorNetto = produktiveKostenTabelle > 0 ? kalkSollumsatzInklFortzahlung / produktiveKostenTabelle : 0; 
    const lohnfaktorBrutto = lohnfaktorNetto * (1 + (effectiveMwstSatz / 100));
    // UI-Defaults (LohnfaktorPage) nutzen 25 %, wenn der Key fehlt — Selektor muss denselben Default fahren,
    // sonst ist LF+Fortzahlung === Brutto und Polster/Ist kollabieren mathematisch.
    const rawSicherheitsfaktor = safeLohnfaktorData?.sicherheitsfaktor;
    const sicherheitsfaktorEffektiv =
        rawSicherheitsfaktor === null || rawSicherheitsfaktor === undefined || rawSicherheitsfaktor === ''
            ? 25
            : parseVal(rawSicherheitsfaktor);
    const lohnfaktorFortzahlung = lohnfaktorBrutto * (1 + sicherheitsfaktorEffektiv / 100);
    // Eisernes Gesetz: Lohnfaktor aktiv = max(3,50; LF+Fortzahlung; Manual).
    // IST-Unternehmerstundenlohn: ausschließlich lohnfaktorFortzahlung (ohne Manual-Floor).
    // Planumsatz/Polster/Leistungspläne: lohnfaktorAktiv (steigt mit Manual mit).
    const LOHNFAKTOR_AKTIV_MIN = 3.5;
    const lohnfaktorAktiv = Math.max(
        LOHNFAKTOR_AKTIV_MIN,
        lohnfaktorFortzahlung,
        parseVal(safeLohnfaktorData.manualLohnfaktor) || 0
    );
    // Excel D14: Planumsatz = (Grundlöhne MA inkl. Bonus + prod. Unternehmerlohn) * LF aktiv / (1+MwSt)
    const planumsatzBasis = produktiveMitarbeiterOhneAzubis.reduce(
        (sum: number, ma: EmployeeData) => sum + (ma.grundlohn || 0) + (ma.bonus || 0),
        prodUnternehmerlohn
    );
    const planumsatzGesamtNetto = (planumsatzBasis * lohnfaktorAktiv) / (1 + (effectiveMwstSatz / 100));
    // Polster = Planumsatz(aktiv) − kalk. Soll inkl. Fortz. — Manual erhöht Planumsatz & Polster
    const differenz = planumsatzGesamtNetto - kalkSollumsatzInklFortzahlung;
    const planumsatzGesamtNettoKalkuliert = planumsatzGesamtNetto;
    const differenzKalkuliert = differenz;
    // IST-Unternehmerstundenlohn: ausschließlich LF+Fortzahlung (inkl. Sicherheitsfaktor)
    const realisierterStundenlohnChef = lohnfaktorFortzahlung > 0 && monatsstundenChef > 0
        ? (kalkulierterUnternehmerUmsatzBrutto / lohnfaktorFortzahlung) / monatsstundenChef
        : 0;

    const teamSize = produktiveMitarbeiterOhneAzubis.length;
    const fallbackCountryLnk =
        activeLand === 'IT'
            ? Math.round(((Number(activeCountrySettings?.ItBz_Faktor_Vollzeit) || 1.80) - 1) * 1000) / 10
            : (activeCountrySettings?.Lohnnebenkosten || 0);
    const avgSozialabgabenPercent = teamSize > 0
        ? (produktiveMitarbeiterOhneAzubis.reduce((s: number, ma: EmployeeData) => s + (ma.lohnnebenkostenSatz || 0), 0) / teamSize)
        : fallbackCountryLnk;
    const lohnfortzahlungPercent = globalSettings.Urlaubsausgleich || 20.0; 
    const summeStundenlohnTeam = produktiveMitarbeiterOhneAzubis.reduce((sum: number, ma: EmployeeData) => sum + (ma.stundenlohnGesamt || 0), 0);
    // Excel: Summe(Azubi-Std-Löhne) * 2/3 — nicht Ø * 2/3
    const avgStundenlohnAzubiAnteil = azubiAssistenzData.length > 0
        ? azubiAssistenzData.reduce((sum: number, ma: EmployeeData) => sum + (ma.stundenlohnGesamt || 0), 0) * (2 / 3)
        : 0;
    const anzahlMitarbeiterProd = produktiveMitarbeiterOhneAzubis.length;
    const anzahlChefProd = prodUnternehmerlohn > 0 ? 1 : 0;
    const anzahlAzubiFaktor = azubiAssistenzData.length;
    const anzahlProduktivesTeamGesamt = anzahlMitarbeiterProd + anzahlChefProd + anzahlAzubiFaktor;
    const teamStundenlohn = anzahlProduktivesTeamGesamt > 0 ? (summeStundenlohnTeam + (unternehmerStundenlohnSoll || 0) + avgStundenlohnAzubiAnteil) / anzahlProduktivesTeamGesamt : 0;

    const geoeffneteStunden = safeUnternehmerData?.geoeffneteStunden || [];
    const summeStundenOeffnung = geoeffneteStunden.reduce((sum: number, stunde: number) => sum + parseVal(stunde), 0);
    const anzahlTageGeoeffnet = geoeffneteStunden.filter((stunde: number) => parseVal(stunde) > 0).length;
    const durchschnittlicheOeffnungszeit = anzahlTageGeoeffnet > 0 ? summeStundenOeffnung / anzahlTageGeoeffnet : 0;
    const durchschnittlicherUmsatzProTag = (teamStundenlohn * durchschnittlicheOeffnungszeit) * lohnfaktorAktiv;

    const calculatedMitarbeiterLeistung: Record<string, MitarbeiterLeistungData & { requiredRate75?: number }> = {};
    const anzahlTeamMembers = produktiveMitarbeiterOhneAzubis.length + (prodUnternehmerlohn > 0 ? 1 : 0);
    const summeStundenTeam = produktiveMitarbeiterOhneAzubis.reduce((s: number, ma: EmployeeData) => s + parseVal(ma.monatsstunden), 0) + (prodUnternehmerlohn > 0 ? monatsstundenChef : 0);
    const avgMonthlyHoursTeam = anzahlTeamMembers > 0 ? summeStundenTeam / anzahlTeamMembers : 0;

    personalData.forEach((ma: EmployeeData) => {
        const raw = safeClientData.mitarbeiterLeistung ? safeClientData.mitarbeiterLeistung[ma.id] : null;
        if (!raw) return; 
        
        const maSollUmsatzMonat = (ma.grundlohn || 0) * lohnfaktorAktiv;
        const maSollUmsatzStd = ma.monatsstunden > 0 ? maSollUmsatzMonat / ma.monatsstunden : 0;
        
        const requiredRate75 = (!auslastungsfaktor || auslastungsfaktor <= 0) ? 0 : maSollUmsatzStd / (auslastungsfaktor / 100);
        
        const allMonths = [...(raw.halbjahr1 || []), ...(safeClientData.isHJ2Freigegeben ? (raw.halbjahr2 || []) : [])];
        const activeMonthsForAvg = allMonths.filter(m => (parseVal(m.bedienUmsatz) + parseVal(m.vkUmsatz)) > 0);
        let avgIstUmsatzProStunde = 0;
        if (activeMonthsForAvg.length > 0) {
            const summeIstUmsatz = activeMonthsForAvg.reduce((sum, m) => sum + (parseVal(m.bedienUmsatz) + parseVal(m.vkUmsatz)), 0);
            avgIstUmsatzProStunde = safeDiv(summeIstUmsatz / activeMonthsForAvg.length, ma.monatsstunden || 1);
        }
        const arbeitszeitTagMitarbeiter = safeDiv(ma.wochenstunden || 0, ma.arbeitstage || 5);
        
        // 💉 DEINE ORIGINAL-FORMEL IST ZURÜCK: Absolute Präzision bei der Effizienz
        const calcStats = (months: MonatsDaten[]) => {
            const activeMonths = months.filter(m => (parseVal(m.bedienUmsatz) + parseVal(m.vkUmsatz)) > 0 || m.festmachen);
            if (activeMonths.length === 0) return { efficiency: 0, auslastung: 0, krank: 0, status: 'neutral' as MitarbeiterStatus, vkFaktor: 0 };
            
            let sumEfficiency = 0; let sumAuslastung = 0; let sumKrank = 0; let totalBedien = 0; let totalVk = 0;
            
            activeMonths.forEach(m => {
                const b = parseVal(m.bedienUmsatz); const v = parseVal(m.vkUmsatz); totalBedien += b; totalVk += v;
                const istUmsatzStd = ma.monatsstunden > 0 ? (b + v + (avgIstUmsatzProStunde * parseVal(m.urlaubsTage) * arbeitszeitTagMitarbeiter)) / ma.monatsstunden : 0;
                sumKrank += parseVal(m.krankheitsTage);
                
                let geleisteteStunden = ma.monatsstunden; 
                if (m.unproduktiveStundenPlus) { geleisteteStunden += parseVal(m.unproduktiveStunden); } else { geleisteteStunden -= parseVal(m.unproduktiveStunden); }
                if (geleisteteStunden <= 0) geleisteteStunden = 1;
                
                // 💉 Die heilige Formel
                sumEfficiency += sollumsatz > 0 ? ((b + v) / sollumsatz) * (avgMonthlyHoursTeam / geleisteteStunden) * (auslastungsfaktor / 100) : 0;
                sumAuslastung += maSollUmsatzStd > 0 ? (istUmsatzStd / maSollUmsatzStd) * auslastungsfaktor : 0;
            });
            
            const finalEfficiency = (sumEfficiency / activeMonths.length) * anzahlTeamMembers;
            const finalAuslastung = sumAuslastung / activeMonths.length;
            
            // HJ-Ampel = Umsatzziel (wie LeistungPage UmsatzIndicator): Ø-Umsatz vs Soll / Kostendeckung
            const avgUmsatzMonat = (totalBedien + totalVk) / activeMonths.length;
            const maKostendeckung = (ma.grundlohn || 0) * kostendeckungsfaktor;
            let status: MitarbeiterStatus = 'neutral';
            if (avgUmsatzMonat <= 0) status = 'neutral';
            else if (avgUmsatzMonat >= maSollUmsatzMonat) status = 'ok';
            else if (avgUmsatzMonat >= maKostendeckung) status = 'warnung';
            else status = 'schlecht';
            return { efficiency: finalEfficiency, auslastung: finalAuslastung, krank: sumKrank, status, vkFaktor: totalBedien > 0 ? totalVk / totalBedien : 0 };
        };

        const statsTotal = calcStats([...(raw.halbjahr1 || []), ...(safeClientData.isHJ2Freigegeben ? (raw.halbjahr2 || []) : [])]);
        
        calculatedMitarbeiterLeistung[ma.id] = {
            halbjahr1: raw.halbjahr1 || [], halbjahr2: raw.halbjahr2 || [],
            calculatedAvgEfficiency: statsTotal.efficiency, calculatedAvgVkFaktor: statsTotal.vkFaktor, calculatedSumKrankTage: statsTotal.krank,
            calculatedStatusHJ1: calcStats(raw.halbjahr1 || []).status, calculatedStatusHJ2: safeClientData.isHJ2Freigegeben ? calcStats(raw.halbjahr2 || []).status : 'neutral',
            calculatedAvgUmsatzauslastung: statsTotal.auslastung, requiredRate75: requiredRate75 
        };
    });
    
    const processULHalbjahr = (halbjahrData: any[]) => {
        const monatsDetails = halbjahrData.map((monat: any) => {
            const isLocked = monat.festmachen === true;
            const snapMetrics = monat.lockedSnapshot?.metrics;

            const activeMonatsstunden = (isLocked && snapMetrics?.monatsstunden !== undefined) ? snapMetrics.monatsstunden : monatsstundenChef;
            const activePlanumsatz = (isLocked && snapMetrics?.planumsatz !== undefined) ? snapMetrics.planumsatz : kalkulierterUnternehmerUmsatzBrutto;
            const activeMwst = (isLocked && snapMetrics?.mwst !== undefined) ? snapMetrics.mwst : effectiveMwstSatz;
            const activeKostendeckung = (isLocked && snapMetrics?.kostendeckung !== undefined) ? snapMetrics.kostendeckung : kostendeckungsumsatz;
            
            const activeSollumsatzPStd = activeMonatsstunden > 0 ? activePlanumsatz / activeMonatsstunden : 0;

            const bedien = parseVal(monat.bedienumsatzBrutto ?? monat.bedienUmsatz);
            const vk = parseVal(monat.vkUmsatzBrutto ?? monat.vkUmsatz);
            const hasUmsatz = (bedien + vk) > 0;
            const hasDays = (parseVal(monat.urlaubsTage) > 0) || (parseVal(monat.krankheitsTage) > 0);
            const isZeileLeer = !hasUmsatz && !hasDays;

            const gesamtumsatzBrutto = isZeileLeer ? NaN : (bedien + vk);
            const vkFaktor = isZeileLeer ? NaN : safeDiv(vk, bedien);
            const umsatzPStd = isZeileLeer ? NaN : safeDiv(gesamtumsatzBrutto, activeMonatsstunden);
            const deltaNetto = isZeileLeer ? NaN : (gesamtumsatzBrutto === 0 ? 0 : safeDiv(gesamtumsatzBrutto - activePlanumsatz, 1 + activeMwst / 100));

            return {
                ...monat,
                isZeileLeer, hasUmsatz, bedien, vk, gesamtumsatzBrutto, vkFaktor, umsatzPStd, deltaNetto,
                activeMonatsstunden, activePlanumsatz, activeMwst, activeKostendeckung, activeSollumsatzPStd
            };
        });

        const summary = monatsDetails.reduce((acc, d) => {
            if (d.isZeileLeer) return acc;
            acc.bedienumsatzBrutto += d.bedien;
            acc.vkUmsatzBrutto += d.vk;
            acc.urlaubsTage += parseVal(d.urlaubsTage);
            acc.krankheitsTage += parseVal(d.krankheitsTage);
            acc.gesamtumsatzBrutto += d.gesamtumsatzBrutto || 0;
            acc.deltaNetto += d.deltaNetto || 0;
            acc.umsatzPStd += d.umsatzPStd || 0;
            acc.activePlanumsatz += d.activePlanumsatz || 0;
            acc.activeKostendeckung += d.activeKostendeckung || 0;
            acc.activeSollumsatzPStd += d.activeSollumsatzPStd || 0;
            return acc;
        }, { bedienumsatzBrutto: 0, vkUmsatzBrutto: 0, urlaubsTage: 0, krankheitsTage: 0, gesamtumsatzBrutto: 0, deltaNetto: 0, umsatzPStd: 0, activePlanumsatz: 0, activeKostendeckung: 0, activeSollumsatzPStd: 0 });

        const monthsWithSales = monatsDetails.filter(d => d.hasUmsatz).length || 1;

        const average = {
            vkFaktor: safeDiv(summary.vkUmsatzBrutto, summary.bedienumsatzBrutto),
            bedienumsatzBrutto: safeDiv(summary.bedienumsatzBrutto, monthsWithSales),
            vkUmsatzBrutto: safeDiv(summary.vkUmsatzBrutto, monthsWithSales),
            urlaubsTage: safeDiv(summary.urlaubsTage, 6),
            krankheitsTage: safeDiv(summary.krankheitsTage, 6),
            gesamtumsatzBrutto: safeDiv(summary.gesamtumsatzBrutto, monthsWithSales),
            umsatzPStd: safeDiv(summary.umsatzPStd, monthsWithSales),
            deltaNetto: safeDiv(summary.deltaNetto, monthsWithSales),
            activePlanumsatz: safeDiv(summary.activePlanumsatz, monthsWithSales),
            activeKostendeckung: safeDiv(summary.activeKostendeckung, monthsWithSales),
            activeSollumsatzPStd: safeDiv(summary.activeSollumsatzPStd, monthsWithSales)
        };

        const calcAuslastungsFaktor = auslastungsfaktor / 100;
        const umsatzAuslastung = calcAuslastungsFaktor * safeDiv(average.umsatzPStd, average.activeSollumsatzPStd);

        return { monatsDetails, summary, average, umsatzAuslastung };
    };

    const processedULHJ1 = processULHalbjahr(safeClientData.ulHalbjahr1 || []);
    const processedULHJ2 = processULHalbjahr(safeClientData.ulHalbjahr2 || []);

    let uIstGesamt = 0;
    let uSollGesamt = 0;

    const addHjToUnternehmer = (hj: any) => {
        hj.monatsDetails.forEach((m: any) => {
            if (m.hasUmsatz || m.festmachen) {
                uIstGesamt += m.gesamtumsatzBrutto || 0;
                uSollGesamt += m.activePlanumsatz || 0;
            }
        });
    };

    addHjToUnternehmer(processedULHJ1);
    if (safeClientData.isHJ2Freigegeben) {
        addHjToUnternehmer(processedULHJ2);
    }

    const unternehmerEfficiency = uSollGesamt > 0 ? uIstGesamt / uSollGesamt : 0;

    return {
        ...defaultCalculations,
        ready: true, 
        clientData: safeClientData, 
        settings: safeSettings, 
        bwaData: safeBwaData, 
        lohnfaktorData: safeLohnfaktorData,
        personalData, unproductivePersonalData, azubiAssistenzData,
        formatCurrency, mwstSatz: effectiveMwstSatz, chVatStatus, activeLand, currencyCode: currencyCode, zeitraum,
        monatsumsatzBrutto, produktiveLohnkostenMitarbeiter: produktiveLohnkostenMitarbeiterSumme, 
        produktiveLohnkostenMitarbeiterOhneAzubis,
        unproduktiveLohnkostenMitarbeiter, azubiKostenGesamt, unprodUnternehmerlohn, unprodUnternehmerlohnPercent,
        gesamtlohn: unternehmerlohnGesamt,
        echterBedarfGesamt,
        bruttoumsatzProStunde: (monatsstundenChef > 0 && kalkulierterUnternehmerUmsatzBrutto > 0) ? kalkulierterUnternehmerUmsatzBrutto / monatsstundenChef : 0,
        erwarteterBruttoUmsatz: kalkulierterUnternehmerUmsatzBrutto,
        erwarteterNettoUmsatz: kalkulierterUnternehmerUmsatzBrutto / (1 + effectiveMwstSatz / 100),
        erwarteteEinkommenssteuer: einkommenssteuerUnternehmer,
        einkommenssteuerUnternehmer,
        einkommenssteuerSatz: steuerSatz,
        
        wochensatz: wochensatz,
        auslastungsfaktor: auslastungsfaktor,
        auslastungsfaktorMax: auslastungsfaktorMax,
        zusatzumsatz: zusatzumsatz,
        gewinnlinie: gewinnlinie,
        investlinie: investlinie,
        liquiditaet: liquiditaet,
        verlustPercent: verlustPercent, 

        realUnternehmerSchnittHJ1: processedULHJ1.average.gesamtumsatzBrutto || 0, 
        realUnternehmerSchnittHJ2: processedULHJ2.average.gesamtumsatzBrutto || 0,
        
        calculatedMitarbeiterLeistung, 
        unternehmerMetrics: { 
            efficiency: unternehmerEfficiency, 
            umsatzauslastung: processedULHJ1.umsatzAuslastung, 
            vkFaktor: processedULHJ1.average.vkFaktor, 
            sumKrankTage: processedULHJ1.summary.krankheitsTage + processedULHJ2.summary.krankheitsTage 
        },
        gesamtUmsatz, monatsumsatzNetto, sollumsatz, ueberschuss, ueberschussProzent,
        foerderzuschussMonat, breakEvenNachFoerderung, sollumsatzVorFoerderung, 
        /** Filialbetriebe: kumulierte Netto-Monatsumsätze (TeamPro+-Schwelle); sonst = monatsumsatzNetto */
        aggregatedMonatsumsatzNetto: (() => {
            if (safeClientData?.hasBranches) {
                const agg = Number(safeClientData.aggregatedNettoMonatsumsatz ?? safeClientData.nettoMonatsumsatz);
                if (Number.isFinite(agg) && agg > 0) return agg;
            }
            return monatsumsatzNetto;
        })(),
        isTeamProPlus: !!(
            safeClientData?.isTeamProPlus
            || safeClientData?.insiderTeamleaderPlusChecked
        ),
        materialkostenMonat, geraetekostenMonat, gemeinkostenGesamtMonat, produktiveKostenGesamtMonat, 
        prodUnternehmerlohn, baseMaterialProzent, geraetekostenGesamt: geraetekostenGesamtSum, betriebskostenMonat, positiveWerteMonat, 
        gemeinkostenfaktor, materialkostenfaktor, geraetekostenfaktor, prodLohnkostenfaktor, 
        einnahmenProMonat: monatsumsatzNetto, ausgabenProMonat: sollumsatz, 
        multiplikator: lohnfaktorNetto, stundenlohnChef: unternehmerStundenlohnSoll, 
        realisierterStundenlohnChef,
        prodWochenstundenChef,
        monatsstundenChef,
        kalkSollumsatzInklFortzahlung, lohnfaktorNetto, lohnfaktorBrutto, lohnfaktorFortzahlung, lohnfaktorAktiv, 
        planumsatzGesamtNetto, planumsatzGesamtNettoKalkuliert, differenz, differenzKalkuliert, produktiveKostenTabelle, lohnfortzahlung, durchschnittlicherUmsatzProTag, 
        kalkulierterUnternehmerUmsatzBrutto, teamStundenlohn,
        avgSozialabgabenPercent, lohnfortzahlungPercent, geraeteListe, gemeinkostenPercent, 
        inhaberErtragSoll, rentabilitaetSoll, inhaberErtragIst, rentabilitaetIst, durchschnittsUmsatzIstNetto,
        summeStundenTeam,
        avgMonthlyHoursTeam,
        anzahlTeamMembers,
        processedULHJ1,
        processedULHJ2
    } as any;
};

export const useCalculationsSelector = () => {
    const clientData = useAppStore(state => state.clientData);
    const settings = useAppStore(state => state.settings);
    
    return useMemo(() => {
        const safeClientData = clientData || defaultClientData;
        const safeSettings = settings || defaultSettings;
        return calculateAllMetrics(safeClientData, safeSettings);
    }, [clientData, settings]);
};

/** Halbjahr-Key für Team-Bonus / Salon-Over-Target-Pool */
export type SalonHalbjahrKey = 'erstesHalbjahr' | 'zweitesHalbjahr';

export interface SalonHalbjahrOverTargetPoolResult {
    halfYear: SalonHalbjahrKey;
    istUmsatzNetto: number;
    /** Σ Verkaufsumsatz netto im Halbjahr */
    vkUmsatzNetto: number;
    /** Σ Mindestumsatz/Kostendeckung ohne Ziel-Polster */
    mindestUmsatzNetto: number;
    /** Unternehmens-Ziel inkl. Ziel-Polster */
    zielUmsatzNetto: number;
    fehlbetrag: number;
    /**
     * Saldo Ist − Kostendeckung (auch negativ).
     * = ueberschussBisZielPolster + ueberschussUeberZiel − defizitUnterKostendeckung
     */
    ueberschussGesamt: number;
    /** Fehlbetrag unter reiner Kostendeckung (Ist < Mindest), positiv als Betrag */
    defizitUnterKostendeckung: number;
    /** Überschuss zwischen Kostendeckung und Ziel-Polster (Rücklagen-/Polster-Zone) */
    ueberschussBisZielPolster: number;
    /** Überschuss über Unternehmens-Ziel (Bonus-relevant für DL-Anteil) */
    ueberschussUeberZiel: number;
    /** Alias: Überschuss über Unternehmens-Ziel */
    ueberschuss: number;
    /** Ziel-Polster in % (Liquidität), dynamisch */
    zielPolsterProzent: number;
    /** Leistungslohn DL-Provision % */
    bonusProzent: number;
    /** Leistungslohn VK-Provision % */
    bonusProzentVK: number;
    /** DL-Anteil am Pool (nur wenn Ziel erreicht): Über-Ziel × DL% */
    earnedPoolDL: number;
    /** VK-Anteil am Pool (nur wenn Ziel erreicht): VK-Netto × VK% */
    earnedPoolVK: number;
    /** Halbjahres-Pool gesamt = DL + VK (0 wenn Ziel nicht erreicht) */
    earnedPool: number;
    /** Monatlicher Auszahlungs-Pool = earnedPool / 6 */
    earnedPoolMonthly: number;
    earnedPoolDLMonthly: number;
    earnedPoolVKMonthly: number;
    targetReached: boolean;
}

/**
 * Salon-Halbjahres-Over-Target-Bonuspool (analog Gesamtleistung-Ziel + Einzel-Leistungslohn-Provision).
 * Ziel_Umsatz = Σ (Mindestumsatz_Monat × (1 + Ziel-Polster%)) ; Pool = max(0, (Ist - Ziel) × Bonus%).
 */
export const computeSalonHalbjahrOverTargetPool = (
    clientData: any,
    settings: any,
    halfYear: SalonHalbjahrKey
): SalonHalbjahrOverTargetPoolResult => {
    const cd = clientData || {};
    const st = settings || {};
    const monthStart = halfYear === 'zweitesHalbjahr' ? 6 : 0;
    const monthEnd = monthStart + 6;

    const landRaw = cd.Land || cd.land || st.selectedCountryId || 'DE';
    const activeLand = normalizeLandCode(landRaw);
    const baseCountry = resolveCountrySettings(st, activeLand);
    const countryOverrides = st.countryOverrides?.[activeLand] || {};
    const country = { ...baseCountry, ...countryOverrides };
    const isMandantUstBefreit = Boolean(
        cd.umsatzsteuerbefreit === true ||
        cd.isUmsatzsteuerbefreit === true ||
        cd.isVatExempt === true ||
        cd.clientData?.umsatzsteuerbefreit === true ||
        cd.clientData?.isUmsatzsteuerbefreit === true ||
        cd.clientData?.isVatExempt === true
    );
    const dbMwSt = country?.MwSt_Satz ?? country?.mwst ?? country?.mwstSatz ?? country?.MwSt;
    const defaultMwSt = activeLand === 'CH' ? 8.1 : activeLand === 'AT' ? 20 : activeLand === 'IT' ? 22 : 19;
    const chPoolVat = activeLand === 'CH'
        ? getEffectiveSwissVatRate(country, cd, parseVal(cd.bwaData?.umsatz))
        : null;
    // PRIO 0: Universeller Hard-Override für ALLE Länder (DE, AT, CH, IT_BZ)
    const mwstSatz = isMandantUstBefreit
        ? 0.0
        : activeLand === 'CH' && chPoolVat
            ? chPoolVat.effectiveRate
            : (dbMwSt !== undefined && dbMwSt !== null ? Number(dbMwSt) : defaultMwSt);
    const mwstFaktor = 1 + (Number.isFinite(mwstSatz) ? mwstSatz : defaultMwSt) / 100;

    // Dynamisches Ziel-Polster: Mandant clientData.liquiditaet, sonst Settings.global.Liquiditaet
    const zielPolsterProzent = parseVal(
        cd.liquiditaet ?? st.global?.Liquiditaet ?? DEFAULT_MANDANTEN_PARAMETER.liquiditaet
    );

    const lf = cd.lohnfaktorData || {};
    const mindestBase =
        halfYear === 'zweitesHalbjahr'
            ? parseVal(lf.mindestumsatz2) || parseVal(lf.mindestumsatz1)
            : parseVal(lf.mindestumsatz1) || parseVal(lf.mindestumsatz2);

    const provBlock =
        halfYear === 'zweitesHalbjahr'
            ? cd.leistungsprovision?.zweitesHalbjahr
            : cd.leistungsprovision?.erstesHalbjahr;
    const bonusProzentRaw = parseVal(provBlock?.provisionDienstleistung);
    const bonusProzentVKRaw = parseVal(provBlock?.provisionVerkauf);
    const effectiveBonusProzent = bonusProzentRaw > 0 ? bonusProzentRaw : 10;
    const effectiveBonusProzentVK = bonusProzentVKRaw > 0 ? bonusProzentVKRaw : 10;
    const bonusSatz = effectiveBonusProzent / 100;
    const bonusSatzVK = effectiveBonusProzentVK / 100;

    const months: any[] = Array.isArray(cd.gesamtleistungData) ? cd.gesamtleistungData : [];
    let istUmsatzNetto = 0;
    let vkUmsatzNetto = 0;
    let mindestUmsatzNetto = 0;
    let zielUmsatzNetto = 0;

    for (let i = monthStart; i < monthEnd; i++) {
        const monat = months[i] || {};
        const isLocked = monat.abgeschlossen === true;
        const snap = monat.frozenCalculations || {};
        const activeMindestBase =
            isLocked && snap.mindestumsatzBase !== undefined
                ? parseVal(snap.mindestumsatzBase)
                : mindestBase;
        const activeMwstFaktor =
            isLocked && snap.mwstFaktor !== undefined ? parseVal(snap.mwstFaktor) || mwstFaktor : mwstFaktor;
        const activeRuecklagenFaktor =
            isLocked && snap.ruecklagenZielFaktor !== undefined
                ? parseVal(snap.ruecklagenZielFaktor)
                : zielPolsterProzent / 100;
        const activeZielFaktor = 1 + activeRuecklagenFaktor;

        const anpassungsFaktor = monat.kostenErhoeht ? -1 : 1;
        const mindestNetto = activeMindestBase + parseVal(monat.aussergewoehnlich) * anpassungsFaktor;
        mindestUmsatzNetto += mindestNetto;
        zielUmsatzNetto += mindestNetto * activeZielFaktor;

        const bedienBrutto = parseVal(monat.bedienumsatzBrutto);
        const vkBrutto = parseVal(monat.vkUmsatzBrutto);
        const gesamtBrutto = bedienBrutto + vkBrutto;
        if (gesamtBrutto > 0) {
            istUmsatzNetto += safeDiv(gesamtBrutto, activeMwstFaktor || 1);
            vkUmsatzNetto += safeDiv(vkBrutto, activeMwstFaktor || 1);
        }
    }

    const deltaVsZiel = istUmsatzNetto - zielUmsatzNetto;
    const targetReached = deltaVsZiel > 0;
    const fehlbetrag = targetReached ? 0 : Math.max(0, -deltaVsZiel);
    const ueberschussGesamt = istUmsatzNetto - mindestUmsatzNetto;

    const defizitUnterKostendeckung = Math.max(0, mindestUmsatzNetto - istUmsatzNetto);
    const ueberschussBisZielPolster = Math.max(
        0,
        Math.min(istUmsatzNetto, zielUmsatzNetto) - mindestUmsatzNetto
    );
    const ueberschussUeberZiel = Math.max(0, istUmsatzNetto - zielUmsatzNetto);
    const ueberschuss = ueberschussUeberZiel;

    // Analog Einzel-Leistungslohn: DL auf Über-Ziel, VK auf VK-Netto — nur bei erreichtem Gesamtziel
    const earnedPoolDL = targetReached ? Math.round(ueberschussUeberZiel * bonusSatz * 100) / 100 : 0;
    const earnedPoolVK = targetReached ? Math.round(vkUmsatzNetto * bonusSatzVK * 100) / 100 : 0;
    const earnedPool = Math.round((earnedPoolDL + earnedPoolVK) * 100) / 100;
    const earnedPoolMonthly = Math.round((earnedPool / 6) * 100) / 100;
    const earnedPoolDLMonthly = Math.round((earnedPoolDL / 6) * 100) / 100;
    const earnedPoolVKMonthly = Math.round((earnedPoolVK / 6) * 100) / 100;

    return {
        halfYear,
        istUmsatzNetto: Math.round(istUmsatzNetto * 100) / 100,
        vkUmsatzNetto: Math.round(vkUmsatzNetto * 100) / 100,
        mindestUmsatzNetto: Math.round(mindestUmsatzNetto * 100) / 100,
        zielUmsatzNetto: Math.round(zielUmsatzNetto * 100) / 100,
        fehlbetrag: Math.round(fehlbetrag * 100) / 100,
        ueberschussGesamt: Math.round(ueberschussGesamt * 100) / 100,
        defizitUnterKostendeckung: Math.round(defizitUnterKostendeckung * 100) / 100,
        ueberschussBisZielPolster: Math.round(ueberschussBisZielPolster * 100) / 100,
        ueberschussUeberZiel: Math.round(ueberschussUeberZiel * 100) / 100,
        ueberschuss: Math.round(ueberschuss * 100) / 100,
        zielPolsterProzent,
        bonusProzent: effectiveBonusProzent,
        bonusProzentVK: effectiveBonusProzentVK,
        earnedPoolDL,
        earnedPoolVK,
        earnedPool,
        earnedPoolMonthly,
        earnedPoolDLMonthly,
        earnedPoolVKMonthly,
        targetReached,
    };
};